import { Text, Divider } from '@hh.ru/magritte-ui';
import VSpacingContainer from 'bloko/blocks/vSpacing/VSpacingContainer';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import { GbbGoodFeature } from 'src/components/Price/Gbb';
import PeriodInDays, { PeriodVariants } from 'src/components/Price/PeriodInDays';
import { usePublicationsCount } from 'src/components/Price/Tariff/usePublicationsCount';
import translation from 'src/components/translation';
import { publicationsCountFields, Tariff } from 'src/models/price/priceZp';
import { BooleanString, IntervalInSecondsString } from 'src/models/price/product.types';
import { ZpTariff } from 'src/models/price/zpTariff';
import { getPublicationFeatures } from 'src/utils/price/products';

import styles from './styles.less';

const TrlKeys = {
    contactsFromResponses: 'price.tariffFeature.publications.contactsFromResponses',
    manualUpdateInterval: {
        [IntervalInSecondsString.OneDay]: 'price.tariffFeature.publications.manualUpdateInterval.1d',
    },
    autoUpdateInterval: {
        [IntervalInSecondsString.SevenDays]: 'price.tariffFeature.publications.autoUpdateInterval.7d',
        [IntervalInSecondsString.ThreeDays]: 'price.tariffFeature.publications.autoUpdateInterval.3d',
    },
    logo: 'price.tariffFeature.publications.logo',
    zpSprint: {
        first7Days: 'price.tariffFeature.publications.zpSprint.first7Days',
        topSearch: 'price.tariffFeature.publications.zpSprint.topSearch',
        colorHighlight: 'price.tariffFeature.publications.zpSprint.colorHighlight',
        vacancyOfTheDay: 'price.tariffFeature.publications.zpSprint.vacancyOfTheDay',
    },
};

const PublicationsFeatures: TranslatedComponent<{ tariff: Tariff }> = ({ tariff, trls }) => {
    const publicationsCount = usePublicationsCount(tariff.product);

    // 10 вакансий «Бизнес»
    const renderPublicationsFeature = () => {
        // Если есть поле выбора, в фичах не выводим.
        const hasCountField = publicationsCountFields.some((fieldName) => tariff.fieldNames.includes(fieldName));
        return !hasCountField && publicationsCount ? <GbbGoodFeature>{publicationsCount}</GbbGoodFeature> : null;
    };

    // Отклики с открытыми контактами на сайте и в телефоне
    const renderContactsFromResponsesFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        return <GbbGoodFeature>{trls[TrlKeys.contactsFromResponses]}</GbbGoodFeature>;
    };

    // Поднятие на первые страницы поиска раз в 24 часа вручную
    const renderManualUpdateIntervalFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const trlKeys = TrlKeys.manualUpdateInterval;
        const interval =
            tariff.product && (getPublicationFeatures(tariff.product)?.manualUpdateInterval as keyof typeof trlKeys);
        return interval && interval in trlKeys ? <GbbGoodFeature>{trls[trlKeys[interval]]}</GbbGoodFeature> : null;
    };

    // Автоподнятие на первые страницы поиска раз в 3 дня
    const renderAutoUpdateIntervalFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const trlKeys = TrlKeys.autoUpdateInterval;
        const interval =
            tariff.product && (getPublicationFeatures(tariff.product)?.autoUpdateInterval as keyof typeof trlKeys);
        return interval && interval in trlKeys ? <GbbGoodFeature>{trls[trlKeys[interval]]}</GbbGoodFeature> : null;
    };

    // Выделение логотипом
    const renderLogoFeature = () => {
        // Бэкофису показываем только ключевые счётные составляющие
        if (tariff.backoffice) {
            return null;
        }
        const hasLogo = tariff.product && getPublicationFeatures(tariff.product)?.logo;
        return hasLogo === BooleanString.True ? <GbbGoodFeature>{trls[TrlKeys.logo]}</GbbGoodFeature> : null;
    };

    // Срок размещения — 30 дней
    const renderPeriodFeature = () => {
        return tariff.product?.period ? (
            <GbbGoodFeature>
                <PeriodInDays period={tariff.product.period} variant={PeriodVariants.PublicationDetailed} />
            </GbbGoodFeature>
        ) : null;
    };

    // ЗП Спринт
    const renderZpSprintFeatures = () => {
        if (tariff.tariffName !== ZpTariff.ZpSprint) {
            return null;
        }
        return (
            <>
                <div className={styles.seven}>
                    <Text>{trls[TrlKeys.zpSprint.first7Days]}</Text>
                    <Divider />
                </div>
                <GbbGoodFeature>{trls[TrlKeys.zpSprint.topSearch]}</GbbGoodFeature>
                <GbbGoodFeature>{trls[TrlKeys.zpSprint.colorHighlight]}</GbbGoodFeature>
                <GbbGoodFeature>{trls[TrlKeys.zpSprint.vacancyOfTheDay]}</GbbGoodFeature>
            </>
        );
    };

    return (
        <VSpacingContainer base={4}>
            {renderPublicationsFeature()}
            {renderContactsFromResponsesFeature()}
            {renderManualUpdateIntervalFeature()}
            {renderAutoUpdateIntervalFeature()}
            {renderLogoFeature()}
            {renderPeriodFeature()}
            {renderZpSprintFeatures()}
        </VSpacingContainer>
    );
};

export default translation(PublicationsFeatures);
